/**
 * IndustryResearchPanel — read-only view of any IndustryDetail tab, scoped
 * to a Case context. Fetches the industry once via the same TanStack Query key
 * used by IndustryDetail so navigating back-and-forth never double-fetches.
 *
 * Unlike IndustryDetail, this panel intentionally omits mutating actions
 * (Refresh Research, Add Competitor, Enrich Landscape, Delete) — it is a
 * read-only mirror suitable for embedding inside CaseDetail tabs.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Zap,
  Shield,
  Users,
  Globe,
  Info,
  ExternalLink,
  Building2,
} from 'lucide-react';
import { getIndustry } from '../../lib/api/industryResearchApi';
import { parseNumber, formatLargeCurrency } from '../../lib/utils';
import type { IndustryDetail as IndustryDetailType } from '../../types/industryResearch';

export type IndustryPanelTab =
  | 'overview'
  | 'market'
  | 'competitive'
  | 'technology'
  | 'disruptions';

interface Props {
  industryId: string;
  tab: IndustryPanelTab;
}

export default function IndustryResearchPanel({ industryId, tab }: Props) {
  const { data: industry, isLoading, error } = useQuery({
    queryKey: ['industry', industryId],
    queryFn: () => getIndustry(industryId),
    enabled: !!industryId,
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) return <PanelSkeleton />;

  if (error || !industry) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 text-sm">Failed to load industry data</p>
      </div>
    );
  }

  switch (tab) {
    case 'overview':    return <OverviewContent industry={industry} />;
    case 'market':      return <MarketContent industry={industry} />;
    case 'competitive': return <CompetitiveContent industry={industry} />;
    case 'technology':  return <TechnologyContent industry={industry} />;
    case 'disruptions': return <DisruptionsContent industry={industry} />;
    default: return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton
// ─────────────────────────────────────────────────────────────────────────────

function PanelSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-lg p-6">
            <div className="h-3 bg-neutral-100 rounded w-1/2 mb-3" />
            <div className="h-6 bg-neutral-200 rounded w-3/4" />
          </div>
        ))}
      </div>
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="h-5 bg-neutral-200 rounded w-1/3 mb-4" />
        <div className="space-y-3">
          {[100, 90, 80].map((w, i) => (
            <div key={i} className="h-3 bg-neutral-100 rounded" style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────────────────

function OverviewContent({ industry }: { industry: IndustryDetailType }) {
  const summary = industry.executiveSummary;

  if (!summary) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Research Not Yet Generated</h3>
        <p className="text-blue-700">
          Navigate to Industry Research and click "Refresh Research" to generate AI-powered analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Industry Health', value: summary.industryHealth, color: 'text-neutral-900' },
          { label: 'Attractiveness', value: summary.industryAttractiveness?.replace('_', ' '), color: 'text-primary-600' },
          { label: 'Opportunity', value: `${(parseNumber(summary.opportunityScore) * 100).toFixed(0)}%`, color: 'text-green-600' },
          { label: 'Risk Score', value: `${(parseNumber(summary.riskScore) * 100).toFixed(0)}%`, color: 'text-orange-600' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
            <div className="text-sm font-medium text-neutral-500">{label}</div>
            <div className={`text-2xl font-bold capitalize mt-3 ${color}`}>{value || '—'}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Executive Summary</h2>
        {summary.overallSummary?.trim() ? (
          <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">{summary.overallSummary}</p>
        ) : (
          <p className="text-neutral-500 italic">Executive summary not yet available.</p>
        )}
      </div>

      {summary.keyInsights && summary.keyInsights.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Key Insights</h2>
          <ul className="space-y-2">
            {summary.keyInsights.map((insight, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <span className="text-neutral-700">{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.strategicImplications && summary.strategicImplications.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Strategic Implications</h2>
          <ul className="space-y-2">
            {summary.strategicImplications.map((imp, i) => (
              <li key={i} className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                <span className="text-neutral-700">{imp}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.criticalSuccessFactors && summary.criticalSuccessFactors.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Critical Success Factors</h2>
          <ul className="space-y-2">
            {summary.criticalSuccessFactors.map((f, i) => (
              <li key={i} className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <span className="text-neutral-700">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Market Analysis
// ─────────────────────────────────────────────────────────────────────────────

function MarketContent({ industry }: { industry: IndustryDetailType }) {
  const market = industry.marketData;

  if (!market) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">Market data available after research generation</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="text-sm text-neutral-600 mb-1">Market Size</div>
          <div className="text-3xl font-bold text-neutral-900">
            {formatLargeCurrency(parseNumber(market.totalMarketSize))}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{market.currency}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="text-sm text-neutral-600 mb-1">Growth Rate (CAGR)</div>
          <div className="text-3xl font-bold text-green-600">
            {parseNumber(market.marketGrowthRate).toFixed(1)}%
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="text-sm text-neutral-600 mb-1">Avg Gross Margin</div>
          <div className="text-3xl font-bold text-blue-600">
            {parseNumber(market.averageGrossMargin).toFixed(1)}%
          </div>
        </div>
      </div>

      {market.marketByRegion && Object.keys(market.marketByRegion).length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Market by Region</h2>
          <div className="space-y-3">
            {Object.entries(market.marketByRegion).map(([region, size]) => (
              <div key={region} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-neutral-400" />
                  <span className="font-medium text-neutral-900">{region}</span>
                </div>
                <span className="text-neutral-700 font-semibold">{formatLargeCurrency(parseNumber(size))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {market.primaryDrivers && market.primaryDrivers.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Primary Growth Drivers</h2>
          <ul className="space-y-2">
            {market.primaryDrivers.map((driver, i) => (
              <li key={i} className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <span className="text-neutral-700">{driver}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {market.fastestGrowingSegment && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">Fastest Growing Segment</h3>
          <p className="text-neutral-700 text-lg">{market.fastestGrowingSegment}</p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Competitive Landscape (read-only — no Add/Enrich actions)
// ─────────────────────────────────────────────────────────────────────────────

function CompetitiveContent({ industry }: { industry: IndustryDetailType }) {
  const competitive = industry.competitiveLandscape;

  if (!competitive) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-10 text-center">
        <Users className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
        <p className="text-neutral-600 font-medium mb-1">No competitive landscape yet</p>
        <p className="text-sm text-neutral-400">
          Navigate to Industry Research → Competitive Landscape to add competitors or generate analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Intensity', value: competitive.competitiveIntensity },
          { label: 'Concentration', value: competitive.marketConcentration },
          { label: 'Top Competitors', value: String(competitive.topCompetitorCount ?? (competitive.marketLeaders?.length ?? 'N/A')) },
          { label: 'Barrier to Entry', value: competitive.barrierToEntry },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
            <div className="text-sm font-medium text-neutral-500">{label}</div>
            <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
              {(value || 'N/A').replace('_', ' ')}
            </div>
          </div>
        ))}
      </div>

      {competitive.marketLeaders && competitive.marketLeaders.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Market Leaders</h2>
          <div className="space-y-4">
            {competitive.marketLeaders.map((leader: any, i: number) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-neutral-900">{leader.companyName}</h3>
                      {leader.existsInCompanyResearch && leader.companyId && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          <Building2 className="w-3 h-3" />
                          In Company Research
                        </span>
                      )}
                    </div>
                    {leader.existsInCompanyResearch && leader.companyId && (
                      <a
                        href={`/companies/${leader.companyId}`}
                        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        View in Company Research
                      </a>
                    )}
                    {leader.strengths && leader.strengths.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-neutral-500">Strengths: </span>
                        <span className="text-xs text-neutral-700">{leader.strengths.slice(0, 2).join(' · ')}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4 shrink-0">
                    <div className="text-2xl font-bold text-primary-600">
                      {parseNumber(leader.marketShare).toFixed(1)}%
                    </div>
                    {leader.revenue && (
                      <div className="text-sm text-neutral-600">
                        {formatLargeCurrency(parseNumber(leader.revenue))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Technology & Trends
// ─────────────────────────────────────────────────────────────────────────────

function TechnologyContent({ industry }: { industry: IndustryDetailType }) {
  const technology = industry.technologyTrends;

  if (!technology) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">Technology data available after research generation</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Adoption Stage', value: technology.adoptionStage },
          { label: 'Digital Maturity', value: technology.digitalMaturity },
          { label: 'R&D Spend Trend', value: technology.rdSpendTrend },
          { label: 'Tech Readiness', value: technology.technologyReadiness },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
            <div className="text-sm font-medium text-neutral-500">{label}</div>
            <div className="text-xl font-bold text-neutral-900 capitalize mt-3">{value || '—'}</div>
          </div>
        ))}
      </div>

      {technology.keyTechnologies && technology.keyTechnologies.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Key Technologies</h2>
          <div className="space-y-4">
            {technology.keyTechnologies.map((tech: any, i: number) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-neutral-900">{tech.name}</h3>
                  {tech.adoptionRate && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {tech.adoptionRate}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {tech.maturityLevel && (
                    <div><span className="text-neutral-600">Maturity: </span><span className="font-medium capitalize">{tech.maturityLevel}</span></div>
                  )}
                  {tech.impact && (
                    <div><span className="text-neutral-600">Impact: </span><span className="font-medium capitalize">{tech.impact}</span></div>
                  )}
                  {tech.timeline && (
                    <div><span className="text-neutral-600">Timeline: </span><span className="font-medium">{tech.timeline}</span></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {technology.innovationDrivers && technology.innovationDrivers.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Innovation Drivers</h2>
          <ul className="space-y-2">
            {technology.innovationDrivers.map((driver: string, i: number) => (
              <li key={i} className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                <span className="text-neutral-700">{driver}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Disruptions & Risks
// ─────────────────────────────────────────────────────────────────────────────

function DisruptionsContent({ industry }: { industry: IndustryDetailType }) {
  const disruptions = industry.disruptions;

  if (!disruptions) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">Disruptions data available after research generation</p>
      </div>
    );
  }

  const severityColor = (s: string) => {
    if (s === 'critical') return 'bg-red-100 text-red-800';
    if (s === 'high')     return 'bg-orange-100 text-orange-800';
    if (s === 'moderate') return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  return (
    <div className="space-y-6">
      {disruptions.disruptionRisk && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Overall Disruption Risk</h2>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-lg font-bold ${severityColor(disruptions.disruptionRisk)}`}>
            <AlertTriangle className="w-6 h-6" />
            {disruptions.disruptionRisk.toUpperCase()}
          </div>
        </div>
      )}

      {disruptions.disruptiveForces && disruptions.disruptiveForces.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Disruptive Forces</h2>
          <div className="space-y-4">
            {disruptions.disruptiveForces.map((force: any, i: number) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">{force.name ?? force.label}</h3>
                    {(force.type || force.timeline) && (
                      <div className="flex items-center gap-2 text-sm text-neutral-600">
                        {force.type && <span className="capitalize">{force.type}</span>}
                        {force.type && force.timeline && <span>•</span>}
                        {force.timeline && <span className="capitalize">{force.timeline}</span>}
                      </div>
                    )}
                  </div>
                  {force.severity && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${severityColor(force.severity)}`}>
                      {force.severity}
                    </span>
                  )}
                </div>
                {force.impact && <p className="text-sm text-neutral-700 mb-2">{force.impact}</p>}
                {force.mitigation && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                    <span className="font-medium text-blue-900">Mitigation: </span>
                    <span className="text-blue-700">{force.mitigation}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {disruptions.regulatoryChanges && disruptions.regulatoryChanges.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">Regulatory Changes</h3>
            <ul className="space-y-2">
              {disruptions.regulatoryChanges.map((change: string, i: number) => (
                <li key={i} className="text-sm text-neutral-700 flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  {change}
                </li>
              ))}
            </ul>
          </div>
        )}
        {disruptions.geopoliticalRisks && disruptions.geopoliticalRisks.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">Geopolitical Risks</h3>
            <ul className="space-y-2">
              {disruptions.geopoliticalRisks.map((risk: string, i: number) => (
                <li key={i} className="text-sm text-neutral-700 flex items-start gap-2">
                  <Globe className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  {risk}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
